import React from 'react';
import {Link} from 'react-router-dom';
import styles from './JuryProjectCard.module.scss';
import {IconStar, IconStarFilled, IconCircleCheck, IconCircleX} from '@tabler/icons-react';
import useJuryStore from '../../../../store/juryStore';
import CardActionButton from './CardActionButton';

function JuryProjectCard({project, showDeleteButton = false}) {
    const inputRef = React.useRef(null);

    const votes = useJuryStore(s => s.votes[project._id] || 0);
    const isBookmarked = useJuryStore(s => s.bookmarks.has(project._id));
    const castVote = useJuryStore(s => s.castVote);
    const toggleBookmark = useJuryStore(s => s.toggleBookmark);
    const voteLimit = useJuryStore(s => s.voteLimit);
    const voteLimitPerProject = useJuryStore(s => s.voteLimitPerProject);
    const allVotes = useJuryStore(s => s.votes);
    const usedPoints = Object.values(allVotes).reduce((s, v) => s + v, 0);
    const canAddVotes = (voteLimit - usedPoints) > 0 && votes < voteLimitPerProject;

    // Get the first image from projectGallery if available
    const projectImageUrl = project.projectGallery && project.projectGallery.length > 0
        ? project.projectGallery[0].url
        : null;
    const categories = Array.isArray(project.categories) ? project.categories : (project.category ? [{name: project.category}] : []);
    const isPublikumspreistraeger = project.isPublikumspreistraeger || false;

    const handleIncrement = (e) => {
        e.preventDefault();
        e.stopPropagation();
        castVote(project._id, 1);
    };

    const handleDecrement = (e) => {
        e.preventDefault();
        e.stopPropagation();
        castVote(project._id, -1);
    };

    const handleBookmark = () => {
        toggleBookmark(project._id);
    };

    const handleDelete = () => {
        if (votes > 0) castVote(project._id, -votes);
    };

    const handleInputBlur = (e) => {
        const value = e.target.value.trim();
        const newValue = value === '' ? 0 : parseInt(value, 10);
        if (!isNaN(newValue) && newValue >= 0) {
            const clamped = Math.min(newValue, voteLimitPerProject);
            const delta = clamped - votes;
            if (delta !== 0) {
                castVote(project._id, delta);
            }
        } else {
            e.target.value = votes;
        }
    };

    const handleInputFocus = (e) => {
        e.target.select();
    };

    const handleInputClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.target.blur();
        }
    };

    return (
        <div className={styles.ProjectCard}>
            <Link to={`/projekte/${project._id}`} className={styles.ProjectCardLink}>
                {/* Top Section for Desktop/Mobile layout differences */}
                <div className={styles.CardTop}>
                    {/* Image Section */}
                    <div className={styles.ImageSection}>
                        <div className={styles.ImagePlaceholder}>
                            {projectImageUrl ? (
                                <img
                                    src={projectImageUrl}
                                    alt={project.contestantName}
                                    className={styles.ProjectImage}
                                />
                            ) : (
                                <div className={styles.NoImage}/>
                            )}
                        </div>

                        {/* Vote Count Badge */}
                        <div className={styles.VoteBadge}>
                            <IconCircleCheck size={20} className={styles.VoteIcon}/>
                            <span>{project.voteCount || 0} Stimmen</span>
                        </div>
                    </div>

                    {/* Institution Name */}
                    <div className={styles.InstitutionSection}>
                        <p className={styles.InstitutionName}>{project.contestantName || 'Institution'}</p>
                    </div>
                </div>

                {/* Project Title */}
                <div className={styles.TitleSection}>
                    <h3 className={styles.ProjectTitle}>{project.projectTitle || 'Projekttitel'}</h3>
                </div>

                {/* Categories/Tags */}
                {categories.length > 0 && (
                    <div className={styles.TagsSection}>
                        {isPublikumspreistraeger && (
                            <span className={`${styles.Tag} ${styles.TagPrimary}`}>
                                Publikumspreisträger
                            </span>
                        )}
                        {categories.slice(0, isPublikumspreistraeger ? 1 : 2).map((category, idx) => (
                            <span key={idx} className={styles.Tag}>
                                {category.name}
                            </span>
                        ))}
                    </div>
                )}
            </Link>

            {/* Actions Section */}
            <div className={styles.ActionsSection}>
                {/* Vote Controls */}
                <div className={styles.VoteControls}>
                    <button
                        className={styles.VoteButton}
                        onClick={handleDecrement}
                        disabled={votes === 0}
                        aria-label="Punkte verringern"
                    >
                        -
                    </button>
                    <input
                        ref={inputRef}
                        type="number"
                        min="0"
                        inputMode="numeric"
                        className={styles.VoteInput}
                        defaultValue={votes}
                        key={votes}
                        onBlur={handleInputBlur}
                        onFocus={handleInputFocus}
                        onClick={handleInputClick}
                        onKeyDown={handleKeyDown}
                        aria-label="Punkte direkt eingeben"
                    />
                    <button
                        className={styles.VoteButton}
                        onClick={handleIncrement}
                        disabled={!canAddVotes}
                        aria-label="Punkte erhöhen"
                    >
                        +
                    </button>
                </div>

                <CardActionButton
                    icon={isBookmarked
                        ? <IconStarFilled size="1.5rem" className={styles.BookmarkIconActive}/>
                        : <IconStar size="1.5rem" className={styles.BookmarkIconInactive}/>
                    }
                    label="Merken"
                    onClick={handleBookmark}
                    ariaLabel={isBookmarked ? 'Von Merkliste entfernen' : 'Zur Merkliste hinzufügen'}
                />

                {showDeleteButton && (
                    <CardActionButton
                        icon={<IconCircleX size="1.5rem"/>}
                        label="Löschen"
                        onClick={handleDelete}
                        ariaLabel="Punkte entfernen"
                        variant="danger"
                    />
                )}
            </div>
        </div>
    );
}

export default JuryProjectCard;
